import { useSelector } from "react-redux";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { useEffect, useMemo, useState } from "react";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { useDispatch } from "react-redux";
import { WizardStepTitle } from "app/modules/shared/wizard/WizardStepTitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { WizardStepperFooter } from "app/modules/shared/wizard/WizardStepFooter";
import { formatDate } from "cpl-react-common/Helpers/StringHelpers";
import {
  selectServiceChargeInvoice,
  setCurrentStep,
  setServiceChargeInvoiceModel,
} from "../../ServiceChargeInvoicingSlice";
import {
  selectLoadingServiceChargeDateCodes,
  selectServiceChargeDateCodes,
  getServiceChargeDateCodes,
} from "./ServiceChargeDateCodesSlice";
import { ServiceChargeInvoicingSteps } from "../../ServiceChargeInvoiceWizardSteps";
import MultiSelectDropdown from "cpl-react-common/components/Inputs/MultiSelectDropdown";

const ServiceChargeDateCodes: React.FC = () => {
  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();
  const serviceChargeInvoice = useSelector(selectServiceChargeInvoice);
  const loadingServiceChargeDateCodes = useSelector(selectLoadingServiceChargeDateCodes);
  const serviceChargeDateCodes = useSelector(selectServiceChargeDateCodes);
  const [defaultDate, setDefaultDate] = useState<Date>(serviceChargeInvoice.chargesTo ?? new Date());
  const [defaultChargeCodes, setDefaultChargeCodes] = useState<string[]>(serviceChargeInvoice.chargeDateCodes ?? []);
  const [dateTouched, setDateTouched] = useState<boolean>(false);

  const disableContinueButton = useMemo(() => {
    return defaultChargeCodes.length > 0 && defaultDate.isValidDate();
  }, [defaultChargeCodes, defaultDate]);

  useEffect(() => {
    thunkDispatch(getServiceChargeDateCodes());
  }, [thunkDispatch]);

  useEffect(() => {
    setDefaultChargeCodes(serviceChargeInvoice.chargeDateCodes ?? []);
    setDefaultDate(serviceChargeInvoice.chargesTo ?? new Date());
  }, [serviceChargeInvoice]);

  function updateDate(newDate: Date) {
    setDateTouched(true);
    setDefaultDate(newDate);
  }

  function goToNextStep() {
    dispatch(
      setServiceChargeInvoiceModel({
        ...serviceChargeInvoice,
        chargeDateCodes: defaultChargeCodes,
        chargesTo: defaultDate,
      })
    );
    dispatch(setCurrentStep(ServiceChargeInvoicingSteps.DevelopmentsOrPropertyManagers));
  }

  return (
    <>
      <WizardStepTitle
        stepNumber={ServiceChargeInvoicingSteps.ServiceChargeDateCodes}
        title="Select Charge Date Codes"
        subtitle="Select the Charge Date Codes that you would like to process and the date that you would like to capture charges to"
      ></WizardStepTitle>
      <ActivityIndicator isBusy={loadingServiceChargeDateCodes}>
        <div className="d-flex flex-column align-items-center">
          <div className="d-flex">
            <label className="fw-bold form-label col-form-label pe-3 required">Select charge date codes:</label>
            <MultiSelectDropdown
              data-test-id="charge-date-codes-step__charge-date-codes-select"
              handleChange={(selections) => {
                setDefaultChargeCodes(selections as string[]);
              }}
              options={serviceChargeDateCodes.map((x) => ({
                value: x.value ?? "",
                label: x.label ?? "",
              }))}
              defaultSome={defaultChargeCodes}
            />
          </div>
          <div className="d-flex">
            <div>
              <label className="fw-bold form-label col-form-label pe-3 required">Capture charges to:</label>
            </div>
            <div>
              <input
                data-test-id="charge-date-codes-step__date-input"
                type="date"
                className="form-control form-control-solid"
                defaultValue={formatDate(defaultDate)}
                onChange={(event) => {
                  updateDate(new Date(event.target.value));
                }}
              ></input>
            </div>
          </div>
          {dateTouched && !defaultDate.isValidDate() && (
            <div className="d-flex">
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">
                  <p>Invalid Date</p>
                </div>
              </div>
            </div>
          )}
        </div>
        <WizardStepperFooter>
          <div className="d-flex justify-content-end">
            <button
              data-test-id="charge-date-codes-step__continue-button"
              className="btn btn-success btn-sm"
              type="button"
              title="Continue"
              onClick={() => goToNextStep()}
              disabled={!disableContinueButton}
            >
              <span className="me-1">Continue</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        </WizardStepperFooter>
      </ActivityIndicator>
    </>
  );
};

export default ServiceChargeDateCodes;
